//src/admin/pages/AdminCreateDiscountCode.js
import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import {
  createAdminCoupon,
  getAdminCouponById,
  updateAdminCoupon,
} from "../../services/adminCoupons.api";
import { FiChevronRight } from "react-icons/fi";
import { FaCalendarAlt, FaClock, FaTag } from "react-icons/fa";

function AdminCreateDiscountCode() {
  const navigate = useNavigate();
  const { id } = useParams();
  const initialRef = React.useRef(null);
  const { lang } = useContext(AdminLanguageContext);

  // usage
const [usageType, setUsageType] = useState("unlimited"); // unlimited | limited

const [isDirty, setIsDirty] = useState(false);

const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
const [pendingNavigation, setPendingNavigation] = useState(null);

// dates (same as order discount)
const [startDate, setStartDate] = useState("");
const [startTime, setStartTime] = useState("");
const [hasEnd, setHasEnd] = useState(false);
const [endDate, setEndDate] = useState("");
const [endTime, setEndTime] = useState("");

// modals
const [dateModalOpen, setDateModalOpen] = useState(false);
const [dateTarget, setDateTarget] = useState(null);

const [timeModalOpen, setTimeModalOpen] = useState(false);
const [timeTarget, setTimeTarget] = useState(null);

const [rangeStart, setRangeStart] = useState(null);
const [rangeEnd, setRangeEnd] = useState(null);

const [tempTime, setTempTime] = useState({
  hour: 12,
  minute: "00",
  period: "AM",
});


  const [form, setForm] = useState({
    code: "",
    discount_type: "percent",
    discount_value: "",
    max_uses: "",
    is_active: true,
  });
  
useEffect(() => {
  if (!id && !initialRef.current) {
    initialRef.current = {
      code: "",
      discount_type: "percent",
      discount_value: "",
      max_uses: "",
      usageType: "unlimited",
      startDate: "",
      startTime: "",
      hasEnd: false,
      endDate: "",
      endTime: "",

      // ✅ ADD THIS LINE
      is_active: true,
    };
  }
}, [id]);

// 🔥 LOAD EDIT DATA
useEffect(() => {
  if (!id) return;

  getAdminCouponById(id)
    .then((coupon) => {
  const nextForm = {
    code: coupon.code || "",
    discount_type: coupon.discount_type || "percent",
    discount_value: coupon.discount_value || "",
    max_uses: coupon.max_uses || "",
    is_active: Boolean(coupon.is_active),
  };

  setForm(nextForm);

  const nextUsageType = coupon.max_uses ? "limited" : "unlimited";
  setUsageType(nextUsageType);

  let nextStartDate = "";
  let nextStartTime = "";
  let nextEndDate = "";
  let nextEndTime = "";
  let nextHasEnd = false;

  if (coupon.start_at) {
    const dt = new Date(coupon.start_at);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const hh = String(dt.getHours()).padStart(2, "0");
    const min = String(dt.getMinutes()).padStart(2, "0");

    nextStartDate = `${yyyy}-${mm}-${dd}`;
    nextStartTime = `${hh}:${min}`;

    setStartDate(nextStartDate);
    setStartTime(nextStartTime);
  } else {
    setStartDate("");
    setStartTime("");
  }

  if (
    coupon.end_at &&
    !new Date(coupon.end_at).toISOString().startsWith("2099-12-31")
  ) {
    const dt = new Date(coupon.end_at);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const hh = String(dt.getHours()).padStart(2, "0");
    const min = String(dt.getMinutes()).padStart(2, "0");

    nextEndDate = `${yyyy}-${mm}-${dd}`;
    nextEndTime = `${hh}:${min}`;
    nextHasEnd = true;

    setEndDate(nextEndDate);
    setEndTime(nextEndTime);
    setHasEnd(true);
  } else {
    setEndDate("");
    setEndTime("");
    setHasEnd(false);
  }

  initialRef.current = {
    code: nextForm.code,
    discount_type: nextForm.discount_type,
    discount_value: String(nextForm.discount_value || ""),
    max_uses: String(nextForm.max_uses || ""),
    usageType: nextUsageType,
    startDate: nextStartDate,
    startTime: nextStartTime,
    hasEnd: nextHasEnd,
    endDate: nextEndDate,
    endTime: nextEndTime,

    // ✅ THIS WAS MISSING
  is_active: nextForm.is_active,
  };

  setIsDirty(false);
})
    .catch(console.error);
}, [id]);

useEffect(() => {
  if (!initialRef.current) return;

  const current = {
    is_active: form.is_active,
    code: form.code || "",
    discount_type: form.discount_type || "percent",
    discount_value: String(form.discount_value || ""),
    max_uses: String(form.max_uses || ""),
    usageType,
    startDate,
    startTime,
    hasEnd,
    endDate,
    endTime,
  };

  const initial = initialRef.current;

  const isChanged = Object.keys(initial).some(
    (key) => String(initial[key]) !== String(current[key])
  );

  setIsDirty(isChanged);
}, [
  form.is_active,
  form.code,
  form.discount_type,
  form.discount_value,
  form.max_uses,
  usageType,
  startDate,
  startTime,
  hasEnd,
  endDate,
  endTime,
]);

// 🔥 TIME MODAL FIX
const openTimeModal = (target) => {
  const sourceTime = target === "start" ? startTime : endTime;

  if (sourceTime) {
    let [hour, minute] = sourceTime.split(":").map(Number);
    let period = "AM";

    if (hour >= 12) period = "PM";

    if (hour === 0) hour = 12;
    else if (hour > 12) hour -= 12;

    setTempTime({
      hour,
      minute: String(minute).padStart(2, "0"),
      period,
    });
  } else {
    setTempTime({
      hour: 12,
      minute: "00",
      period: "AM",
    });
  }

  setTimeTarget(target);
  setTimeModalOpen(true);
};

  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);


  const generateCode = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";

  for (let i = 0; i < 8; i++) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }

  setForm(prev => ({ ...prev, code: result }));
};

  const card =
    "bg-white border border-[#e1e3e5] rounded-[18px] shadow-sm p-4 space-y-4";

  const input =
    "w-full h-[42px] rounded-[12px] border border-[#c9cccf] px-3 text-sm outline-none focus:border-[#008060]";

    const handleNavigate = (path) => {
  if (!isDirty) {
    navigate(path);
  } else {
    setPendingNavigation(() => () => navigate(path));
    setShowLeaveConfirm(true);
  }
};

 const handleSave = async () => {
  if (!form.code || !form.discount_value) return;

  try {
    setSaving(true);

    const start_at = startDate
      ? `${startDate} ${startTime || "00:00"}:00`
      : null;

    const end_at =
      hasEnd && endDate
        ? `${endDate} ${endTime || "00:00"}:00`
        : null;

    const payload = {
      ...form,
      max_uses:
        usageType === "unlimited"
          ? null
          : Number(form.max_uses || 0),
      start_at,
      end_at,
    };

    if (id) {
      await updateAdminCoupon(id, payload);
    } else {
      await createAdminCoupon(payload);
    }

    initialRef.current = {
  code: payload.code || "",
  discount_type: payload.discount_type || "percent",
  discount_value: String(payload.discount_value || ""),
  max_uses: String(payload.max_uses || ""),
  usageType,
  startDate,
  startTime,
  hasEnd,
  endDate,
  endTime,
};

setIsDirty(false);

    navigate("/admin/discounts");
  } catch (err) {
    console.error(err);
  } finally {
    setSaving(false);
  }
};

const formatDisplayDate = (date) => {
  if (!date) return new Date().toLocaleDateString();
  return new Date(date).toLocaleDateString();
};

const formatDisplayTime = (time) => {
  if (!time) {
    return new Date().toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  const [h, m] = time.split(":");
  const d = new Date();
  d.setHours(h, m);

  return d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
};

const CalendarRangePicker = ({ rangeStart, setRangeStart }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

  const startDay = startOfMonth.getDay();

  const days = [];

  for (let i = 0; i < startDay; i++) days.push(null);

  for (let d = 1; d <= endOfMonth.getDate(); d++) {
    days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), d));
  }

  const isSameDay = (a, b) =>
    a && b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

    

  return (
    <div>
      <div className="flex justify-between items-center mb-3 px-2">
        <button onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}>‹</button>

        <div className="font-semibold text-sm">
          {currentMonth.toLocaleString(lang === "AR" ? "ar" : "en", {
  month: "long",
  year: "numeric",
})}
        </div>

        <button onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}>›</button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((day, i) => {
          if (!day) return <div key={i} />;

          const selected = isSameDay(day, rangeStart);

          return (
            <button
              key={i}
              onClick={() => setRangeStart(day)}
              className={`h-8 rounded-md text-sm ${
                selected ? "bg-black text-white" : "hover:bg-gray-200"
              }`}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const TimePicker = ({ tempTime, setTempTime }) => {
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = ["00", "15", "30", "45"];
  const periods = ["AM", "PM"];

  return (
    <div className="flex gap-3">

      {/* HOURS */}
      <div className="flex-1">
        <div className="text-xs text-gray-500 mb-2">Hour</div>
        <div className="grid grid-cols-3 gap-2">
          {hours.map(h => (
            <button
              key={h}
              onClick={() => setTempTime(prev => ({ ...prev, hour: h }))}
              className={`h-9 rounded-lg text-sm ${
                tempTime.hour === h ? "bg-black text-white" : "hover:bg-gray-200"
              }`}
            >
              {h}
            </button>
          ))}
        </div>
      </div>

      {/* MINUTES */}
      <div className="flex-1">
        <div className="text-xs text-gray-500 mb-2">Min</div>
        <div className="flex flex-col gap-2">
          {minutes.map(m => (
            <button
              key={m}
              onClick={() => setTempTime(prev => ({ ...prev, minute: m }))}
              className={`h-9 rounded-lg text-sm ${
                tempTime.minute === m ? "bg-black text-white" : "hover:bg-gray-200"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* AM / PM */}
      <div className="flex flex-col gap-2">
        {periods.map(p => (
          <button
            key={p}
            onClick={() => setTempTime(prev => ({ ...prev, period: p }))}
            className={`h-9 px-3 rounded-lg text-sm ${
              tempTime.period === p ? "bg-black text-white" : "hover:bg-gray-200"
            }`}
          >
            {p}
          </button>
        ))}
      </div>

    </div>
  );
};

const buildSummary = () => {
  if (!form.discount_value) {
    return lang === "AR"
      ? "ابدأ في إعداد كود الخصم..."
      : "Start configuring your code";
  }

  const valueText =
    form.discount_type === "percent"
      ? lang === "AR"
        ? `خصم ${form.discount_value}%`
        : `${form.discount_value}% off`
      : lang === "AR"
        ? `خصم ${form.discount_value}$`
        : `$${form.discount_value} off`;

  const codeText = form.code || (lang === "AR" ? "الكود الخاص بك" : "your code");

  return lang === "AR"
    ? `هذا الكود يقدم ${valueText} عند استخدام "${codeText}".`
    : `This code gives ${valueText} when customers use "${codeText}".`;
};

  return (
    <div className={`flex bg-[#f1f1f1] min-h-screen`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className="flex-1 lg:ml-64">
        <AdminHeader
  setIsOpen={setIsOpen}
  customCenter={
    <div
      className={`
        hidden sm:block
        w-full
        sm:max-w-[500px]
        max-w-full
        transition-all duration-300
        ${isDirty ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"}
      `}
    >
      <div className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-md px-3 py-2 sm:flex sm:items-center sm:justify-between">

        {/* LEFT */}
        <div className="flex items-center gap-2 text-[13px] text-gray-200">
          <span>
            {lang === "AR"
                ? id ? "تعديلات غير محفوظة" : "كود غير محفوظ"
                : id ? "Unsaved changes" : "Unsaved code"}
          </span>
        </div>

        {/* RIGHT */}
        <div className="flex gap-2">

          <button
           onClick={() => handleNavigate("/admin/discounts")}
            className="px-2 py-[2px] text-[12px] rounded-md border border-[#3a3a3a] bg-[#1f1f1f] text-gray-200"
          >
            {lang === "AR" ? "إلغاء" : "Discard"}
          </button>

          <button
            onClick={handleSave}
            className="px-2 py-[2px] text-[12px] rounded-md bg-white text-black"
          >
            {lang === "AR" ? "حفظ" : "Save"}
          </button>

        </div>
      </div>
    </div>
  }
/>

        <main className="p-4 sm:p-6">
          <div className="max-w-[900px] mx-auto">

            {/* HEADER */}
            <div className="flex items-center gap-2 mb-5">
              <button
                onClick={() => handleNavigate("/admin/discounts")}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-200"
              >
                <img
  src="/icons/discountNotSelected.png"
  alt="discount"
  className="w-[18px] h-[18px] object-contain"
/>
              </button>

              <FiChevronRight />

              <span className="font-semibold">
                {lang === "AR" ? "إنشاء كود خصم" : "Create discount code"}
              </span>
            </div>

<div className="sm:hidden mb-4 flex gap-2">
  <button
    type="button"
    onClick={() => handleNavigate("/admin/discounts")}
    className="flex-1 h-[42px] rounded-[12px] border border-[#c9cccf] bg-white text-[#202223] font-medium"
  >
    {lang === "AR" ? "إلغاء" : "Discard"}
  </button>

  <button
    type="button"
    onClick={handleSave}
    disabled={saving || !form.code || !form.discount_value}
    className={`flex-1 h-[42px] rounded-[12px] font-medium transition ${
      saving || !form.code || !form.discount_value
        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
        : "bg-[#202223] text-white hover:bg-black"
    }`}
  >
    {saving
      ? (lang === "AR" ? "جارٍ الحفظ..." : "Saving...")
      : (lang === "AR" ? "حفظ" : "Save")}
  </button>
</div>

           <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-3">
            {/* LEFT */}
            <div className="space-y-3">
         <div className={card}>
  <h3 className="text-[15px] font-semibold">
    {lang === "AR" ? "كود الخصم" : "Discount code"}
  </h3>

  <div className="flex gap-2">
    <input
      className={input}
      value={form.code}
      onChange={(e) => {
        setForm({ ...form, code: e.target.value.toUpperCase() })
         
      }}
      placeholder="SUMMER20"
    />

    <button
      onClick={generateCode}
      className="px-3 h-[42px] bg-[#202223] text-white rounded-[12px] text-sm"
    >
      {lang === "AR" ? "توليد" : "Generate"}
    </button>
  </div>
</div>

      <div className={card}>
  <h3 className="text-[15px] font-semibold">
     {lang === "AR" ? "قيمة الخصم" : "Discount value"}
  </h3>

  <div className="flex gap-3 items-center">

    <div className="flex border border-[#c9cccf] rounded-[12px] overflow-hidden h-[42px]">

  <button
  type="button"
  onClick={() => {
    setForm({ ...form, discount_type: "percent" });
  }}
  className={`
    px-3 sm:px-4
    text-xs sm:text-sm
    whitespace-nowrap
    font-medium
    transition
    ${form.discount_type === "percent"
      ? "bg-[#e5e5e5] text-[#202223]"
      : "bg-white text-[#202223] hover:bg-[#f6f6f7]"}
  `}
>
  {lang === "AR" ? "نسبة" : "Percentage"}
</button>

  <div className="w-[1px] bg-[#c9cccf]" />

  <button
  type="button"
  onClick={() => {
    setForm({ ...form, discount_type: "fixed" });
  }}
  className={`
    px-3 sm:px-4
    text-xs sm:text-sm
    whitespace-nowrap
    font-medium
    transition
    ${form.discount_type === "fixed"
      ? "bg-[#e5e5e5] text-[#202223]"
      : "bg-white text-[#202223] hover:bg-[#f6f6f7]"}
  `}
>
  {lang === "AR" ? "مبلغ ثابت" : "Fixed amount"}
</button>

</div>

    <div className="relative w-[140px]">
      <input
        type="number"
        className={input}
        value={form.discount_value}
        onChange={(e) => {
          setForm({ ...form, discount_value: e.target.value })
         
}}
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2">
        {form.discount_type === "fixed" ? "$" : "%"}
      </span>
    </div>

  </div>
</div>

<div className={card}>
  <h3 className="text-[15px] font-semibold">
    {lang === "AR" ? "عدد الاستخدامات" : "Usage limits"}
  </h3>

  <div className="space-y-3">

  {/* UNLIMITED */}
  <label className="flex items-center gap-3 cursor-pointer">
    <input
      type="radio"
      checked={usageType === "unlimited"}
      onChange={() => {
        setUsageType("unlimited");
        
      }}
    />

    <div>
      <span className="text-sm font-medium">
  {lang === "AR" ? "بدون حد" : "No limit"}
</span>

<p className="text-[12px] text-[#6d7175]">
  {lang === "AR"
    ? "يمكن للعملاء استخدام هذا الكود بدون حد."
    : "Customers can use this code unlimited times."}
</p>
    </div>
  </label>

  {/* LIMITED */}
  <label className="flex items-start gap-3 cursor-pointer">
    <input
      type="radio"
      checked={usageType === "limited"}
      onChange={() => {
        setUsageType("limited");
        
      }}
      className="mt-1"
    />

    <div className="flex-1 space-y-2">
      <div>
        <span className="text-sm font-medium">
  {lang === "AR" ? "تحديد عدد الاستخدامات" : "Limit number of uses"}
</span>

<p className="text-[12px] text-[#6d7175]">
  {lang === "AR"
    ? "حدد عدد المرات التي يمكن استخدام هذا الكود فيها."
    : "Restrict how many times this code can be used."}
</p>
      </div>

      {usageType === "limited" && (
        <input
          type="number"
          className={"max-w-[200px] " + input}
          value={form.max_uses}
          onChange={(e) => {
            setForm({ ...form, max_uses: e.target.value });
     
          }}
        />
      )}
    </div>
  </label>

</div>
</div>

{/* DATES */}
<div className={card}>
  <h3 className="text-[15px] font-semibold">
  {lang === "AR" ? "تواريخ التفعيل" : "Active dates"}
</h3>

  <div className="space-y-3">

    {/* START */}
   <div>
  <label className="text-xs text-[#6d7175] mb-1 block">
    {lang === "AR" ? "تاريخ البدء" : "Start date"}
  </label>

  <div className="grid grid-cols-2 gap-3">
    <button
      type="button"
      onClick={() => {
        setDateTarget("start");
        setDateModalOpen(true);
      }}
      className="
        min-w-0
        flex items-center gap-2
        h-[42px]
        rounded-[12px]
        border border-[#c9cccf]
        px-3
        text-sm
        bg-white
        hover:border-[#008060]
      "
    >
      <FaCalendarAlt className="shrink-0 text-[#6d7175]" />
      <span className="truncate">
        {formatDisplayDate(startDate)}
      </span>
    </button>

    <button
      type="button"
      onClick={() => {
        setTimeTarget("start");
        setTempTime({ hour: 12, minute: "00", period: "AM" });
        setTimeModalOpen(true);
      }}
      className="
        min-w-0
        flex items-center gap-2
        h-[42px]
        rounded-[12px]
        border border-[#c9cccf]
        px-3
        text-sm
        bg-white
        hover:border-[#008060]
      "
    >
      <FaClock className="shrink-0 text-[#6d7175]" />
      <span className="truncate">
        {formatDisplayTime(startTime)}
      </span>
    </button>
  </div>
</div>

    {/* END TOGGLE */}
    <label className="flex items-center gap-2 text-sm mt-2 cursor-pointer">
      <input
        type="checkbox"
        checked={hasEnd}
        onChange={() => {
          setHasEnd(!hasEnd);
          
        }}
      />
     {lang === "AR" ? "تحديد تاريخ انتهاء" : "Set end date"}
    </label>

    {/* END DATE */}
   {hasEnd && (
  <div className="mt-3">
    <label className="text-xs text-[#6d7175] mb-1 block">
      {lang === "AR" ? "تاريخ الانتهاء" : "End date"}
    </label>

    <div className="grid grid-cols-2 gap-3">
      <button
        type="button"
        onClick={() => {
          setDateTarget("end");
          setDateModalOpen(true);
        }}
        className="
          min-w-0
          flex items-center gap-2
          h-[42px]
          rounded-[12px]
          border border-[#c9cccf]
          px-3
          text-sm
          bg-white
          hover:border-[#008060]
        "
      >
        <FaCalendarAlt className="shrink-0 text-[#6d7175]" />
        <span className="truncate">
          {formatDisplayDate(endDate)}
        </span>
      </button>

      <button
        type="button"
        onClick={() => {
          setTimeTarget("end");
          setTempTime({ hour: 12, minute: "00", period: "AM" });
          setTimeModalOpen(true);
        }}
        className="
          min-w-0
          flex items-center gap-2
          h-[42px]
          rounded-[12px]
          border border-[#c9cccf]
          px-3
          text-sm
          bg-white
          hover:border-[#008060]
        "
      >
        <FaClock className="shrink-0 text-[#6d7175]" />
        <span className="truncate">
          {formatDisplayTime(endTime)}
        </span>
      </button>
    </div>
  </div>
)}

  </div>
</div>

            </div>
            {/* RIGHT */}
<div className="space-y-3">

{/* STATUS (EDIT ONLY) */}
{id && (
  <div className={card}>
    <h3 className="text-[15px] font-semibold text-[#202223]">
      {lang === "AR" ? "الحالة" : "Status"}
    </h3>

    <p className="text-[13px] text-[#6d7175]">
      {lang === "AR"
        ? "حدد ما إذا كان الكود نشطًا أو غير نشط"
        : "Control whether this code is active or inactive"}
    </p>

    <div className="relative">
      <select
        value={form.is_active ? "active" : "inactive"}
        onChange={(e) => {
          setForm(prev => ({
            ...prev,
            is_active: e.target.value === "active"
          }));
        }}
        className="appearance-none w-full h-[42px] rounded-[12px] border border-[#c9cccf] px-3 text-sm bg-white"
      >
        <option value="active">
          {lang === "AR" ? "نشط" : "Active"}
        </option>

        <option value="inactive">
          {lang === "AR" ? "غير نشط" : "Inactive"}
        </option>
      </select>

      {/* Shopify arrow */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6d7175]">
        ▾
      </div>
    </div>

    {/* BADGE */}
    <div className="mt-2">
      <span
        className={`
          text-[11px] px-2.5 py-[3px] rounded-md font-medium
          ${form.is_active
            ? "bg-green-50 text-green-700"
            : "bg-gray-100 text-gray-600"}
        `}
      >
        {form.is_active
          ? (lang === "AR" ? "نشط" : "Active")
          : (lang === "AR" ? "غير نشط" : "Inactive")}
      </span>
    </div>
  </div>
)}

  <div className={card}>
  <h3 className="text-[15px] font-semibold">
    {lang === "AR" ? "الملخص" : "Summary"}
  </h3>

  <div
    dir={lang === "AR" ? "rtl" : "ltr"}
    className="bg-[#f6f6f7] border rounded-[14px] p-4 text-sm leading-relaxed"
  >
    {buildSummary()}
  </div>
</div>

</div>
           </div>

          </div>
          {dateModalOpen && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
  onClick={() => setDateModalOpen(false)}>
    <div
  className="w-[340px] bg-white rounded-[18px] shadow-[0_30px_60px_rgba(0,0,0,0.16)] overflow-hidden"
  onClick={(e) => e.stopPropagation()}
>

      <div className="px-5 py-4 border-b">
        <h3 className="text-sm font-semibold">
          {lang === "AR" ? "اختر التاريخ" : "Select date"}
        </h3>
      </div>

      <div className="p-4">
        <CalendarRangePicker
          rangeStart={rangeStart}
          setRangeStart={setRangeStart}
        />
      </div>

      <div className="px-4 py-3 flex justify-end gap-2 border-t">
        <button
          onClick={() => setDateModalOpen(false)}
          className="h-[36px] px-4 border rounded-lg"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          onClick={() => {
            if (!rangeStart) return;

            const date = `${rangeStart.getFullYear()}-${String(
              rangeStart.getMonth() + 1
            ).padStart(2, "0")}-${String(rangeStart.getDate()).padStart(2, "0")}`;

            if (dateTarget === "start") setStartDate(date);
            if (dateTarget === "end") setEndDate(date);

           
            setDateModalOpen(false);
          }}
          className="h-[36px] px-4 bg-black text-white rounded-lg"
        >
          {lang === "AR" ? "تطبيق" : "Apply"}
        </button>
      </div>
    </div>
  </div>
)}

{timeModalOpen && (
 <div
  className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
  onClick={() => setTimeModalOpen(false)}
>
    <div
  className="w-[320px] bg-white rounded-[18px] shadow-[0_30px_60px_rgba(0,0,0,0.16)]"
  onClick={(e) => e.stopPropagation()}
>

      <div className="px-5 py-4 border-b">
        <h3 className="text-sm font-semibold">
          {lang === "AR" ? "اختر الوقت" : "Select time"}
        </h3>
      </div>

      <div className="p-4">
        <TimePicker
          tempTime={tempTime}
          setTempTime={setTempTime}
        />
      </div>

      <div className="px-4 py-3 flex justify-end gap-2 border-t">
        <button
          onClick={() => setTimeModalOpen(false)}
          className="h-[36px] px-4 border rounded-lg"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          onClick={() => {
            let hour = tempTime.hour;

            if (tempTime.period === "PM" && hour !== 12) hour += 12;
            if (tempTime.period === "AM" && hour === 12) hour = 0;

            const final = `${String(hour).padStart(2, "0")}:${tempTime.minute}`;

            if (timeTarget === "start") setStartTime(final);
            if (timeTarget === "end") setEndTime(final);

            
            setTimeModalOpen(false);
          }}
          className="h-[36px] px-4 bg-black text-white rounded-lg"
        >
          {lang === "AR" ? "تم" : "Done"}
        </button>
      </div>
    </div>
  </div>
)}

{showLeaveConfirm && (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
    <div className="w-full max-w-[420px] bg-white rounded-[18px] border border-[#e1e3e5] shadow-xl">

      <div className="px-5 py-4 border-b">
        <h3 className="text-lg font-semibold">
          {lang === "AR" ? "مغادرة الصفحة؟" : "Leave page?"}
        </h3>
      </div>

      <div className="p-5 text-sm text-[#202223]">
        {lang === "AR"
          ? "لديك تغييرات غير محفوظة. إذا غادرت سيتم فقدانها."
          : "You have unsaved changes. If you leave, they will be lost."}
      </div>

      <div className="px-5 py-4 border-t flex justify-end gap-3">
        <button
          onClick={() => setShowLeaveConfirm(false)}
          className="px-4 py-2 border rounded"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          onClick={() => {
            setShowLeaveConfirm(false);
            pendingNavigation && pendingNavigation();
          }}
          className="px-4 py-2 bg-black text-white rounded"
        >
          {lang === "AR" ? "مغادرة" : "Leave"}
        </button>
      </div>
    </div>
  </div>
)}

        </main>
      </div>
    </div>
  );
}

export default AdminCreateDiscountCode;